// ─── REST API client ─────────────────────────────────────

import type { Channel, Message, User, DmChannel, ChannelFile } from '../types';
import type { AgentActivityPlanEntry } from '../types/ws-frames';
import type { NotifPref } from './notif_pref';
import {
  getBorgeeTransport,
  type ApiBytesResponse,
  type ApiResponse,
  type BorgeeRequestBody,
  type BorgeeResponseType,
  type MultipartFilePart,
} from '../client-core/transport';

let currentUserId: string | null = null;

export function setDevUserId(userId: string | null): void {
  currentUserId = userId;
}

export function getDevUserId(): string | null {
  return currentUserId;
}

interface ApiRequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string | BorgeeRequestBody;
  responseType?: BorgeeResponseType;
  signal?: AbortSignal;
}

async function requestResponse(url: string, opts: ApiRequestOptions = {}): Promise<ApiResponse> {
  const body =
    opts.body === undefined
      ? undefined
      : typeof opts.body === 'string'
    ? { kind: 'json' as const, value: opts.body }
    : opts.body;
  return getBorgeeTransport().request({
    path: url,
    method: opts.method,
    headers: opts.headers,
    body,
    responseType: opts.responseType,
    signal: opts.signal,
  });
}

async function request<T>(url: string, opts: ApiRequestOptions = {}): Promise<T> {
  const res = await requestResponse(url, opts);
  if (res.status < 200 || res.status >= 300) {
    const body = jsonData<{ error?: string; code?: string }>(res);
    throw new ApiError(res.status, body?.error ?? (res.statusText || 'Request failed'), body?.code);
  }
  return jsonData<T>(res) as T;
}

function jsonData<T>(response: ApiResponse): T | undefined {
  return response.kind === 'json' ? (response.data as T) : undefined;
}

export type ExecutionModelUsage = {
  provider: string;
  model: string;
  usage_report_count: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
};

export type ExecutionSummary = {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  /** Immutable model-call reports included in this rollup. */
  usage_report_count: number;
  models: ExecutionModelUsage[];
  /** Offers admitted to the execution ledger. */
  execution_count: number;
  started_count: number;
  finished_count: number;
  abandoned_count: number;
  coverage: 'complete' | 'partial' | 'unavailable';
};

export function getExecutionSummary(conversationId: string): Promise<ExecutionSummary> {
  return request<ExecutionSummary>(
    `/api/v1/channels/${encodeURIComponent(conversationId)}/execution-summary`,
  );
}

/** Task rollup is addressed by Task ID, never by its thread/channel ID. */
export function getTaskExecutionSummary(taskId: string): Promise<ExecutionSummary> {
  return request<ExecutionSummary>(`/api/v1/tasks/${encodeURIComponent(taskId)}/execution-summary`);
}

/** One chart-safe execution point. No message, prompt, session, or telemetry capability data is exposed. */
export type ExecutionListEntry = {
  execution_id: string;
  conversation_id: string;
  admitted_at: number;
  started_at: number | null;
  ended_at: number | null;
  terminal_kind: 'finished' | 'abandoned' | null;
  succeeded: boolean | null;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  usage_report_count: number;
};

export type ExecutionListPage = {
  executions: ExecutionListEntry[];
  has_more: boolean;
  /** Pass to the next request's `before` option to retrieve older executions. */
  next_cursor?: string;
};

export type ExecutionListOptions = {
  limit?: number;
  before?: string;
};

function executionListQuery(options: ExecutionListOptions): string {
  const query = new URLSearchParams();
  if (options.limit !== undefined) query.set('limit', String(options.limit));
  if (options.before) query.set('before', options.before);
  const encoded = query.toString();
  return encoded ? `?${encoded}` : '';
}

/** Lists executions for a channel and the child threads visible to this reader. */
export function listChannelExecutions(
  channelId: string,
  options: ExecutionListOptions = {},
): Promise<ExecutionListPage> {
  return request<ExecutionListPage>(
    `/api/v1/channels/${encodeURIComponent(channelId)}/executions${executionListQuery(options)}`,
  );
}

/** Lists executions for exactly this task's server-linked thread. */
export function listTaskExecutions(
  taskId: string,
  options: ExecutionListOptions = {},
): Promise<ExecutionListPage> {
  return request<ExecutionListPage>(
    `/api/v1/tasks/${encodeURIComponent(taskId)}/executions${executionListQuery(options)}`,
  );
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Auth ──────────────────────────────────────────────

export async function login(email: string, password: string): Promise<User> {
  const data = await request<{ user: User }>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  return data.user;
}

export async function fetchHumanSessionStatus(): Promise<{ authenticated: boolean }> {
  return request<{ authenticated: boolean }>('/api/v1/auth/status');
}

export interface GuildInviteLanding {
  guild_id: string;
  guild_name: string;
  inviter_name: string;
  expires_at: number;
  available: boolean;
}

export async function fetchCurrentGuildInvite(): Promise<GuildInviteLanding> {
  const data = await request<{ invite: GuildInviteLanding }>('/api/v1/invites/current');
  return data.invite;
}

export interface GuildInviteAcceptance {
  guild_id: string;
  grant_id: string;
  replay: boolean;
  allow_owned_agents: boolean;
}

export async function acceptCurrentGuildInvite(): Promise<GuildInviteAcceptance> {
  const data = await request<{ acceptance: GuildInviteAcceptance }>('/api/v1/invites/accept', {
    method: 'POST',
  });
  return data.acceptance;
}

export async function logout(): Promise<void> {
  await request<{ ok: boolean }>('/api/v1/auth/logout', {
    method: 'POST',
  });
}

export interface MicrosoftOIDCStatus {
  enabled: boolean;
}

export async function fetchMicrosoftOIDCStatus(): Promise<MicrosoftOIDCStatus> {
  return request<MicrosoftOIDCStatus>('/api/auth/microsoft/status');
}

const microsoftErrorMessages: Record<string, string> = {
  email_conflict: 'This email already belongs to a Borgee account. Continue with password login.',
  account_deleted: 'This Microsoft account belongs to a deleted Borgee account.',
  identity_unnamed:
    'Borgee cannot create an account for this Microsoft account: it has no name or email address to show. Add one to your Microsoft profile, or use password login.',
  upstream_denied: 'Microsoft sign-in was cancelled.',
  upstream_unavailable: 'Microsoft sign-in is temporarily unavailable. Password login still works.',
  upstream_invalid_token: 'Microsoft could not verify this sign-in. Please try again.',
  upstream_invalid_metadata:
    'Microsoft sign-in is temporarily unavailable. Password login still works.',
  provider_disabled: 'Microsoft sign-in is not enabled. Password login still works.',
  invalid_return_to: 'This Microsoft sign-in return path is invalid. Please start again.',
  session_unavailable: 'Borgee could not create a local session. Please try again.',
  nonce_mismatch: 'Microsoft could not verify this sign-in. Please start again.',
  invalid_flow: 'This Microsoft sign-in link is invalid or has already been used.',
  flow_expired: 'This Microsoft sign-in link expired. Please start again.',
  database_busy: 'Microsoft sign-in is temporarily busy. Please try again.',
  oidc_unavailable: 'Microsoft sign-in is temporarily unavailable. Password login still works.',
};

export function microsoftOIDCErrorMessage(code: string | null): string {
  if (!code) return '';
  // `code` is attacker-chosen query input, so the lookup must be an own-property
  // check: a plain object literal answers `__proto__` and `constructor` with
  // inherited values that are not strings.
  if (!Object.hasOwn(microsoftErrorMessages, code)) {
    return 'Microsoft sign-in could not be completed. Password login still works.';
  }
  return microsoftErrorMessages[code];
}

// ─── Channels ───────────────────────────────────────────

export async function fetchChannels(q?: string): Promise<{ channels: Channel[] }> {
  const url = q ? `/api/v1/channels?q=${encodeURIComponent(q)}` : '/api/v1/channels';
  return request<{ channels: Channel[] }>(url);
}

export async function createChannel(
  guildId: string,
  name: string,
  topic?: string,
  memberIds?: string[],
  visibility?: 'public' | 'private',
): Promise<Channel> {
  const data = await request<{ channel: Channel }>('/api/v1/channels', {
    method: 'POST',
    body: JSON.stringify({ guild_id: guildId, name, topic, member_ids: memberIds, visibility }),
  });
  return data.channel;
}

export async function updateChannel(
  channelId: string,
  updates: {
    name?: string;
    topic?: string;
    visibility?: 'public' | 'private' | 'creator_only';
    archived?: boolean;
  },
): Promise<Channel> {
  if (
    updates.topic !== undefined &&
    !updates.name &&
    !updates.visibility &&
    updates.archived === undefined
  ) {
    const data = await request<{ channel: Channel }>(`/api/v1/channels/${channelId}/topic`, {
      method: 'PUT',
      body: JSON.stringify({ topic: updates.topic }),
    });
    return data.channel;
  }
  const data = await request<{ channel: Channel }>(`/api/v1/channels/${channelId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return data.channel;
}

// CHN-1.2 设计 ⑤: archive flip is server-stamped. Pass `true` to retire,
// `false` to un-archive. Server emits a system DM to channel members on the
// `false → true` transition so that everyone observes the closure.
export async function archiveChannel(channelId: string, archived: boolean): Promise<Channel> {
  return updateChannel(channelId, { archived });
}

export interface TurnRoutingPolicy {
  channel_id: string;
  enabled: boolean;
}

// Settings-only API: the server authorizes every read/write with the existing
// channel-manage permission and never includes this policy in broad channel data.
export async function getTurnRoutingPolicy(channelId: string): Promise<TurnRoutingPolicy> {
  return request<TurnRoutingPolicy>(`/api/v1/channels/${channelId}/turn-routing-policy`);
}

export async function setTurnRoutingPolicy(
  channelId: string,
  enabled: boolean,
): Promise<TurnRoutingPolicy> {
  return request<TurnRoutingPolicy>(`/api/v1/channels/${channelId}/turn-routing-policy`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
}

// CHN-7: mute / unmute a channel for the current user. User API only;
// no admin bypass route (ADM-0 §1.3 separation constraint + design ②). Server toggles bit 1
// of user_channel_layout.collapsed and preserves bit 0 for CHN-3 collapse.
export async function muteChannel(
  channelId: string,
  muted: boolean,
): Promise<{ collapsed: number; muted: boolean }> {
  const method = muted ? 'POST' : 'DELETE';
  return request<{ collapsed: number; muted: boolean }>(`/api/v1/channels/${channelId}/mute`, {
    method,
  });
}

// CHN-9: set channel visibility (one of 'creator_only' / 'private' /
// 'public'). User API only, owner-only via the existing
// channel.manage_visibility permission. Keep the CHN-1.2 ACL byte-identical;
// there is no admin bypass route (ADM-0 §1.3 separation constraint). Reuse updateChannel.
export async function setChannelVisibility(
  channelId: string,
  visibility: import('./visibility').ChannelVisibility,
): Promise<Channel> {
  return updateChannel(channelId, { visibility });
}

// CHN-5: list the user's archived channels (owner-only — server filters
// to the caller's membership). Single-source API client; components
// must NOT inline fetch this path (grep 检查守, content-lock §3 设计 ⑤).
export async function listArchivedChannels(): Promise<Channel[]> {
  const data = await request<{ channels: Channel[] }>('/api/v1/me/archived-channels');
  return data.channels;
}

// CHN-6: pin / unpin a channel for the current user. User API only;
// no admin bypass route (ADM-0 §1.3 separation constraint + design ②). Server stamps
// position = -(nowMs) on pin and max(positive)+1.0 on unpin, complementing
// the CHN-3.3 MIN-1.0 monotonic decimal ordering model.
export async function pinChannel(
  channelId: string,
  pinned: boolean,
): Promise<{ position: number; pinned: boolean }> {
  const method = pinned ? 'POST' : 'DELETE';
  return request<{ position: number; pinned: boolean }>(`/api/v1/channels/${channelId}/pin`, {
    method,
  });
}

// CHN-8: set notification preference for a channel. User API only;
// no admin bypass route (ADM-0 §1.3 separation constraint + design ②). pref ∈ {'all', 'mention', 'none'}.
export async function setNotificationPref(
  channelId: string,
  pref: NotifPref,
): Promise<{ collapsed: number; pref: NotifPref }> {
  return request<{ collapsed: number; pref: NotifPref }>(
    `/api/v1/channels/${channelId}/notification-pref`,
    {
      method: 'PUT',
      body: JSON.stringify({ pref }),
    },
  );
}

export async function joinChannel(channelId: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/channels/${channelId}/join`, {
    method: 'POST',
  });
}

export async function fetchChannelPreview(
  channelId: string,
): Promise<{ messages: Message[]; channel: Channel }> {
  return request<{ messages: Message[]; channel: Channel }>(
    `/api/v1/channels/${channelId}/preview`,
  );
}

export async function leaveChannel(channelId: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/channels/${channelId}/leave`, {
    method: 'POST',
  });
}

export async function deleteChannel(channelId: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/channels/${channelId}`, {
    method: 'DELETE',
  });
}

export interface MovedChannelResult {
  channel_id: string;
  target_guild_id: string;
  source_subtree_channel_ids: string[];
}

export async function moveChannel(
  sourceGuildId: string,
  channelId: string,
  targetGuildId: string,
): Promise<MovedChannelResult> {
  return request<MovedChannelResult>(`/api/v1/guilds/${sourceGuildId}/channels/${channelId}/move`, {
    method: 'POST',
    body: JSON.stringify({ target_guild_id: targetGuildId }),
  });
}

export async function reorderChannel(channelId: string, afterId: string | null): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/channels/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ channel_id: channelId, after_id: afterId }),
  });
}

// ─── Channel Members ────────────────────────────────────

export type RequireMentionPolicy = 'inherit' | 'on' | 'off';

export interface ChannelMember {
  user_id: string;
  display_name: string;
  role: string;
  avatar_url?: string | null;
  joined_at: number;
  // CHN-1.2 设计 ⑥ (concept-model §1.4): when true, this member does not
  // auto-broadcast on lifecycle events. Default false for humans; backfilled
  // to true for agents — UI renders a "silent" badge so peers know the agent
  // listens but won't chime in unprompted.
  silent?: boolean;
  require_mention_policy?: RequireMentionPolicy;
  effective_require_mention?: boolean;
  // AL-3.3 agent member presence fold — server attaches the live runtime
  // state for `role=='agent'` rows so MemberPresence has a REST fallback
  // when the WS presence cache is cold (just-opened modal, before any WS
  // frame mirror). Mirrors DmPeer.state — same fold path on the server.
  // Undefined / absent on human rows.
  state?: AgentRuntimeState;
  reason?: AgentRuntimeReason;
  state_updated_at?: number;
  owner_id?: string | null;
  owner_name?: string | null;
}

export interface ChannelMemberRequireMentionState {
  channel_id: string;
  user_id: string;
  require_mention_policy: RequireMentionPolicy;
  effective_require_mention: boolean;
}

export async function fetchChannelMembers(channelId: string): Promise<ChannelMember[]> {
  const data = await request<{ members: ChannelMember[] }>(`/api/v1/channels/${channelId}/members`);
  return data.members;
}

export async function addChannelMember(channelId: string, userId: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/channels/${channelId}/members`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
}

export async function removeChannelMember(channelId: string, userId: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/channels/${channelId}/members/${userId}`, {
    method: 'DELETE',
  });
}

export async function setChannelMemberRequireMentionPolicy(
  channelId: string,
  userId: string,
  policy: RequireMentionPolicy,
): Promise<ChannelMemberRequireMentionState> {
  return request<ChannelMemberRequireMentionState>(
    `/api/v1/channels/${channelId}/members/${userId}/require-mention`,
    {
      method: 'PUT',
      body: JSON.stringify({ policy }),
    },
  );
}

// ─── Messages ───────────────────────────────────────────

/**
 * `includeDeleted` asks the server to return soft-deleted messages as
 * tombstones — identity and timestamps, no body. Only a read that reconciles a
 * window it already showed wants them: a message deleted while the client was
 * not listening would otherwise stay on screen with its body intact.
 *
 * `beforeId` / `afterId` name the row the bound was read off. A millisecond
 * holds as many messages as a burst of writers puts in it, so a page chained on
 * the timestamp alone resumes past the whole millisecond and never shows the
 * boundary row's siblings.
 */
export async function fetchMessages(
  channelId: string,
  opts?: {
    before?: number;
    beforeId?: string;
    after?: number;
    afterId?: string;
    limit?: number;
    includeDeleted?: boolean;
  },
): Promise<{ messages: Message[]; has_more: boolean }> {
  const params = new URLSearchParams();
  if (opts?.before) params.set('before', String(opts.before));
  if (opts?.beforeId) params.set('before_id', opts.beforeId);
  if (opts?.after) params.set('after', String(opts.after));
  if (opts?.afterId) params.set('after_id', opts.afterId);
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.includeDeleted) params.set('include_deleted', 'true');
  const qs = params.toString();
  return request<{ messages: Message[]; has_more: boolean }>(
    `/api/v1/channels/${channelId}/messages${qs ? `?${qs}` : ''}`,
  );
}

export async function sendMessage(
  channelId: string,
  content: string,
  contentType: 'text' | 'image' = 'text',
): Promise<Message> {
  const data = await request<{ message: Message }>(`/api/v1/channels/${channelId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ content, content_type: contentType }),
  });
  return data.message;
}

export type UploadFile = MultipartFilePart['file'];

function multipartFile(name: string, file: UploadFile): MultipartFilePart {
  return { name, file };
}

/**
 * Send a message with file attachments via multipart/form-data.
 * Uses the same POST /channels/:id/messages endpoint but with multipart encoding.
 */
export async function sendMessageWithAttachments(
  channelId: string,
  content: string,
  files: UploadFile[],
  replyToId?: string,
): Promise<Message> {
  const payload: Record<string, unknown> = { content, content_type: 'text' };
  if (replyToId) payload.reply_to_id = replyToId;
  const parts: BorgeeRequestBody = {
    kind: 'multipart',
    parts: [
      { name: 'payload_json', value: JSON.stringify(payload) },
      ...files.map((file, i) => multipartFile(`files[${i}]`, file)),
    ],
  };
  const data = await request<{ message: Message }>(`/api/v1/channels/${channelId}/messages`, {
    method: 'POST',
    body: parts,
  });
  return data.message;
}

export async function editMessage(messageId: string, content: string): Promise<Message> {
  const data = await request<{ message: Message }>(`/api/v1/messages/${messageId}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
  return data.message;
}

export async function deleteMessage(messageId: string): Promise<void> {
  const res = await requestResponse(`/api/v1/messages/${messageId}`, {
    method: 'DELETE',
  });
  if (res.status < 200 || res.status >= 300) {
    const body = jsonData<{ error?: string; code?: string }>(res) ?? { error: res.statusText };
    throw new ApiError(res.status, body.error ?? 'Request failed');
  }
}

// ─── Users ──────────────────────────────────────────────

export async function fetchMe(): Promise<User> {
  const data = await request<{ user: User }>('/api/v1/users/me');
  return data.user;
}

// --- Guild API ---

export interface Guild {
  id: string;
  name: string;
  icon: string;
  owner_id: string;
  created_at: number;
  updated_at: number;
}

export async function fetchGuilds(): Promise<Guild[]> {
  const data = await request<{ guilds: Guild[] }>('/api/v1/guilds');
  return data.guilds;
}

export interface CreateGuildInput {
  name: string;
  icon: string;
}

export interface CreateGuildResponse {
  guild: Guild;
  landing_channel_id: string;
}

export async function createGuild(
  input: CreateGuildInput,
  idempotencyKey: string,
): Promise<CreateGuildResponse> {
  return request<CreateGuildResponse>('/api/v1/guilds', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(input),
  });
}

export async function fetchGuild(guildId: string): Promise<Guild> {
  const data = await request<{ guild: Guild }>(`/api/v1/guilds/${guildId}`);
  return data.guild;
}

export interface UpdateGuildInput {
  name?: string;
  icon?: string;
}

/** Updates the current Server through the owner-authorized Server contract. */
export async function updateGuild(guildId: string, input: UpdateGuildInput): Promise<Guild> {
  const data = await request<{ guild: Guild }>(`/api/v1/guilds/${encodeURIComponent(guildId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return data.guild;
}

export async function fetchGuildChannels(guildId: string): Promise<{ channels: Channel[] }> {
  return request<{ channels: Channel[] }>(`/api/v1/guilds/${encodeURIComponent(guildId)}/channels`);
}

export async function addGuildMember(guildId: string, userId: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/guilds/${encodeURIComponent(guildId)}/members`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
}

export async function removeGuildMember(guildId: string, userId: string): Promise<void> {
  await request<{ ok: boolean }>(
    `/api/v1/guilds/${encodeURIComponent(guildId)}/members/${encodeURIComponent(userId)}`,
    {
      method: 'DELETE',
    },
  );
}

export interface HumanRemovalPreview {
  human_id: string;
  agent_ids: string[];
  agents: Array<{ user_id: string; display_name: string; channel_names: string[] }>;
  channel_count: number;
  thread_count: number;
}

export async function fetchHumanRemovalPreview(
  guildId: string,
  userId: string,
): Promise<HumanRemovalPreview> {
  const data = await request<{ preview: HumanRemovalPreview }>(
    `/api/v1/guilds/${encodeURIComponent(guildId)}/members/${encodeURIComponent(userId)}/removal-preview`,
  );
  return data.preview;
}

export async function removeHumanGuildMemberCascade(
  guildId: string,
  userId: string,
): Promise<void> {
  await request<{ ok: boolean }>(
    `/api/v1/guilds/${encodeURIComponent(guildId)}/members/${encodeURIComponent(userId)}/cascade`,
    { method: 'DELETE' },
  );
}

export interface GuildInviteLink {
  id: string;
  guild_id: string;
  expires_at: number;
  max_uses: number;
  consumed_uses: number;
  revoked_at?: number;
  allow_owned_agents: boolean;
  created_at: number;
}

export async function createGuildInvite(
  guildId: string,
  input: { expires_at?: number; max_uses?: number; allow_owned_agents: boolean },
): Promise<{ invite: GuildInviteLink; url: string }> {
  return request<{ invite: GuildInviteLink; url: string }>(
    `/api/v1/guilds/${encodeURIComponent(guildId)}/invite-links`,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
}

export async function listGuildInvites(guildId: string): Promise<GuildInviteLink[]> {
  const data = await request<{ invites: GuildInviteLink[] }>(
    `/api/v1/guilds/${encodeURIComponent(guildId)}/invite-links`,
  );
  return data.invites;
}

export async function revokeGuildInvite(guildId: string, inviteId: string): Promise<void> {
  await request<unknown>(
    `/api/v1/guilds/${encodeURIComponent(guildId)}/invite-links/${encodeURIComponent(inviteId)}`,
    { method: 'DELETE' },
  );
}

export async function grantInviteOwnedAgent(
  grantId: string,
  userId: string,
): Promise<{ changed: boolean }> {
  return request<{ changed: boolean }>(
    `/api/v1/invite-grants/${encodeURIComponent(grantId)}/agents`,
    {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    },
  );
}

// --- Thread API ---

export async function createThreadFromMessage(
  channelId: string,
  messageId: string,
  name: string,
): Promise<Channel> {
  return request<Channel>(`/api/v1/channels/${channelId}/messages/${messageId}/threads`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function createStandaloneThread(channelId: string, name: string): Promise<Channel> {
  return request<Channel>(`/api/v1/channels/${channelId}/threads`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function fetchActiveThreads(channelId: string): Promise<{ threads: Channel[] }> {
  return request<{ threads: Channel[] }>(`/api/v1/channels/${channelId}/threads/active`);
}

export async function joinThread(threadId: string): Promise<void> {
  await request<void>(`/api/v1/channels/${threadId}/thread-members/@me`, { method: 'PUT' });
}

export async function leaveThread(threadId: string): Promise<void> {
  await request<void>(`/api/v1/channels/${threadId}/thread-members/@me`, { method: 'DELETE' });
}

export async function getChannel(channelId: string): Promise<{ channel: Channel }> {
  return request<{ channel: Channel }>(`/api/v1/channels/${channelId}`);
}

// ── Tasks ──────────────────────────────────────────────

/** Compact execution aggregate embedded in Task list responses. Model-level
 * attribution is intentionally loaded only after the user opens Usage. */
export type TaskUsageSummary = Omit<ExecutionSummary, 'models'>;

export interface PersonalOverviewTask extends Task {
  channel?: {
    guild_id: string;
    guild_name: string;
    channel_id: string;
    channel_name: string;
  };
}

export interface PersonalOverview {
  next_action: { kind: string; title: string; description: string; href: string } | null;
  work: PersonalOverviewTask[];
  work_total: number;
  agents: Array<{
    id: string;
    display_name: string;
    avatar_url: string;
    disabled: boolean;
    state: AgentRuntimeState;
    reason?: AgentRuntimeReason;
  }>;
  agents_total: number;
  computers: Array<{ id: string; machine_name: string; last_seen_at: number | null }>;
  computers_total: number;
  collaborators: Array<{
    id: string;
    display_name: string;
    role: string;
    avatar_url: string;
    task_count: number;
    task_title: string;
    thread_id: string;
    updated_at: number;
  }>;
  activity: Array<{ date: string; messages: number; tasks_created: number }>;
  activity_total: number;
}

export async function fetchPersonalOverview(): Promise<PersonalOverview> {
  return request<PersonalOverview>('/api/v1/me/overview');
}

export interface TaskPerson {
  id: string;
  display_name: string;
  role: string;
  avatar_url: string;
}

/** Display-ready provenance included only by the personal My Tasks API. */
export interface TaskChannelContext {
  guild_id: string;
  guild_name: string;
  channel_id: string;
  channel_name: string;
}

/** Why a task is present in the authenticated user's personal inbox. */
export interface TaskRelationship {
  creator: boolean;
  assignee: boolean;
  participant: boolean;
  owned_agent: boolean;
}

export interface Task {
  id: string;
  channel_id: string;
  guild_id: string;
  seq: number;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'in_review' | 'done' | 'cancelled';
  assignee_id?: string;
  created_by: string;
  message_id?: string;
  thread_id?: string;
  created_at: number;
  updated_at: number;
  heartbeat_interval_ms?: number;
  heartbeat_prompt?: string;
  /** Scheduling may be paused without discarding interval/prompt configuration. */
  heartbeat_enabled?: boolean;
  participants?: string[];
  /**
   * Registered property keys and their values (PR link, issue link, agent
   * session id). Always present; a task with none carries {}.
   */
  properties: Record<string, string>;
  /** Present for task-backed threads in list responses; avoids per-row HTTP. */
  usage_summary?: TaskUsageSummary;
  /** /me/tasks embeds display-safe context so the browser does not join it. */
  channel?: TaskChannelContext;
  assignee?: TaskPerson;
  participant_profiles?: TaskPerson[];
  relationship?: TaskRelationship;
}

/** How to present a property value. The value itself is always a string. */
export type TaskPropertyRender = 'url' | 'identifier' | 'text';

/**
 * One entry of the server's task-property registry. The UI renders from this
 * rather than a hardcoded key list, so the server can register a new property
 * without a client release.
 */
export interface TaskPropertyDefinition {
  key: string;
  label: string;
  render: TaskPropertyRender;
  read_only?: boolean;
}

export async function createTask(
  channelId: string,
  body: {
    title: string;
    description?: string;
    assignee_id?: string;
    heartbeat_interval_ms?: number;
    heartbeat_prompt?: string;
    heartbeat_enabled?: boolean;
    execution?: {
      workspace_source: 'repository_mapping';
      repository: string;
    };
  },
): Promise<Task> {
  return request<Task>(`/api/v1/channels/${channelId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface RepositoryExecutionCapability {
  supported: boolean;
  online: boolean;
  reason?: string;
  repositories: string[];
}

export async function fetchRepositoryExecutionCapability(
  channelId: string,
  assigneeId: string,
  signal?: AbortSignal,
): Promise<RepositoryExecutionCapability> {
  return request<RepositoryExecutionCapability>(
    `/api/v1/channels/${channelId}/task-repository-capability?assignee_id=${encodeURIComponent(assigneeId)}`,
    { signal },
  );
}

export async function fetchChannelTasks(channelId: string): Promise<{ tasks: Task[] }> {
  return request<{ tasks: Task[] }>(`/api/v1/channels/${channelId}/tasks`);
}

/** Resolves the Task attached to a Task Thread through the server's
 * authorization-enforced reverse lookup. */
export async function getTaskByThread(threadId: string): Promise<Task> {
  return request<Task>(`/api/v1/threads/${threadId}/task`);
}

/** Cross-Server personal inbox. The server applies membership and originating
 * Channel/Thread authorization before returning the complete visible set. */
export interface MyTasksResponse {
  tasks: Task[];
}

export async function fetchMyTasks(): Promise<MyTasksResponse> {
  return request<MyTasksResponse>('/api/v1/me/tasks');
}

export async function fetchGuildTasks(guildId: string): Promise<{ tasks: Task[] }> {
  return request<{ tasks: Task[] }>(`/api/v1/guilds/${guildId}/tasks`);
}

export async function updateTask(
  taskId: string,
  updates: {
    status?: string;
    assignee_id?: string;
    title?: string;
    description?: string;
    heartbeat_interval_ms?: number;
    heartbeat_prompt?: string;
    heartbeat_enabled?: boolean;
  },
): Promise<Task> {
  return request<Task>(`/api/v1/tasks/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function convertTaskRepositoryMapping(
  taskId: string,
  body: { repository: string; expected_assignee_id: string },
): Promise<Task> {
  return request<Task>(`/api/v1/tasks/${taskId}/repository-mapping`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function deleteTask(taskId: string): Promise<void> {
  await request(`/api/v1/tasks/${taskId}`, { method: 'DELETE' });
}

// Properties are addressed one key at a time: a write names the key it
// replaces, so a concurrent writer of a different key is never disturbed.

export async function setTaskProperty(taskId: string, key: string, value: string): Promise<Task> {
  return request<Task>(`/api/v1/tasks/${taskId}/properties/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  });
}

export async function deleteTaskProperty(taskId: string, key: string): Promise<Task> {
  return request<Task>(`/api/v1/tasks/${taskId}/properties/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  });
}

export async function fetchTaskPropertyDefinitions(): Promise<TaskPropertyDefinition[]> {
  const data = await request<{ definitions: TaskPropertyDefinition[] }>(
    '/api/v1/task-property-definitions',
  );
  return data.definitions ?? [];
}

export async function fetchGuildMembers(guildId: string): Promise<ChannelMember[]> {
  const data = await request<{ members: ChannelMember[] }>(`/api/v1/guilds/${guildId}/members`);
  return data.members ?? [];
}

export interface MemberInsight {
  user_id: string;
  display_name: string;
  role: string;
  avatar_url: string;
  message_count: number;
  task_count: number;
  task_done_count: number;
}

export interface DailyActivity {
  date: string;
  count: number;
}

export interface TaskParticipant {
  user_id: string;
  display_name: string;
  avatar_url: string;
}

export interface RecentTask {
  id: string;
  title: string;
  status: string;
  channel_id: string;
  thread_id: string;
  participants: TaskParticipant[] | null;
  updated_at: number;
}

export interface TokenUsageBreakdown {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
}
export interface InsightsTaskUsage extends TokenUsageBreakdown {
  task_id: string;
  title: string;
  channel_id: string;
  thread_id: string;
  usage_report_count: number;
}
export interface InsightsUsage extends TokenUsageBreakdown {
  usage_report_count: number;
  execution_count: number;
  started_count: number;
  finished_count: number;
  abandoned_count: number;
  token_trend: Array<TokenUsageBreakdown & { date: string }>;
  model_mix: Array<TokenUsageBreakdown & { provider: string; model: string }>;
  top_tasks: InsightsTaskUsage[];
}
export interface GuildInsights {
  members: MemberInsight[];
  daily_activity: DailyActivity[];
  recent_tasks: RecentTask[];
  usage: InsightsUsage;
}

export async function fetchGuildInsights(
  guildId: string,
  channelId?: string,
): Promise<GuildInsights> {
  const params = channelId ? `?channel_id=${encodeURIComponent(channelId)}` : '';
  return request<GuildInsights>(`/api/v1/guilds/${encodeURIComponent(guildId)}/insights${params}`);
}

export async function updateProfile(data: { display_name?: string }): Promise<User> {
  const res = await request<{ user: User }>('/api/v1/users/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return res.user;
}

// ─── Online ─────────────────────────────────────────────

export async function fetchOnlineUsers(): Promise<string[]> {
  const data = await request<{ user_ids: string[] }>('/api/v1/online');
  return data.user_ids;
}

// ─── Channel read ───────────────────────────────────────

export async function markChannelRead(channelId: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/channels/${channelId}/read`, {
    method: 'PUT',
  });
}

// ─── DM ─────────────────────────────────────────────────

export async function createOrGetDm(userId: string): Promise<DmChannel> {
  const data = await request<{
    channel: DmChannel['id'] extends string
      ? { id: string; name: string; type: 'dm'; created_at: number; created_by: string }
      : never;
    peer: DmChannel['peer'];
  }>(`/api/v1/dm/${userId}`, { method: 'POST' });
  return {
    id: data.channel.id,
    name: data.channel.name,
    type: 'dm',
    created_at: data.channel.created_at,
    peer: data.peer,
    unread_count: 0,
    last_message: null,
  };
}

export async function fetchDmChannels(): Promise<DmChannel[]> {
  const data = await request<{ channels: DmChannel[] }>('/api/v1/dm');
  return data.channels;
}

// ─── Permissions ────────────────────────────────────────

export interface PermissionDetail {
  id: number;
  permission: string;
  scope: string;
  granted_by: string | null;
  granted_at: number;
}

export interface MyPermissionsResponse {
  user_id: string;
  role: string;
  permissions: string[];
  details: PermissionDetail[];
}

export async function fetchMyPermissions(
  path = '/api/v1/me/permissions',
): Promise<MyPermissionsResponse> {
  return request<MyPermissionsResponse>(path);
}

// ─── Auth Register ─────────────────────────────────────

export async function register(
  inviteCode: string,
  email: string,
  password: string,
  displayName: string,
): Promise<User> {
  const data = await request<{ user: User }>('/api/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify({ invite_code: inviteCode, email, password, display_name: displayName }),
  });
  return data.user;
}

// ─── Reactions ─────────────────────────────────────────

export async function addReaction(messageId: string, emoji: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/messages/${messageId}/reactions`, {
    method: 'PUT',
    body: JSON.stringify({ emoji }),
  });
}

export async function removeReaction(messageId: string, emoji: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/messages/${messageId}/reactions`, {
    method: 'DELETE',
    body: JSON.stringify({ emoji }),
  });
}

// DM-5: aggregated reaction summary (CV-7 既有 GET endpoint, 0 server code).
// Response shape mirrors server `store.AggregatedReaction`.
export interface AggregatedReaction {
  emoji: string;
  count: number;
  user_ids: string[];
}

export async function getMessageReactions(
  messageId: string,
): Promise<{ reactions: AggregatedReaction[] }> {
  return request<{ reactions: AggregatedReaction[] }>(`/api/v1/messages/${messageId}/reactions`);
}

// ─── Agents ────────────────────────────────────────────

// AL-1a (#R3 Phase 2) — runtime tri-state plus failure reason code.
// Locked labels live in packages/client/src/lib/agent-state.ts (#190 §11).
// The server always emits state (online/offline/error); reason is present
// only for the error state.
// AL-1b (#R3 Phase 4) 扩 'busy' / 'idle' — server GET /agents/:id/status 5-state
// 合并优先级 (error > busy > idle > online > offline), 见 al-1b-spec.md §1.
export type AgentRuntimeState = 'online' | 'offline' | 'error' | 'busy' | 'idle';
export type AgentRuntimeReason =
  | 'api_key_invalid'
  | 'quota_exceeded'
  | 'network_unreachable'
  | 'runtime_crashed'
  | 'runtime_timeout'
  | 'unknown';

export interface AgentTaskStatus {
  state: AgentRuntimeState;
  last_task_id?: string;
  state_updated_at?: number;
}

export async function fetchAgentTaskStatus(agentId: string): Promise<AgentTaskStatus> {
  return request<AgentTaskStatus>(`/api/v1/agents/${agentId}/status`);
}

export interface Agent {
  id: string;
  display_name: string;
  role: string;
  avatar_url: string | null;
  owner_id: string | null;
  created_at: number;
  // `api_key` is populated ONLY on create / rotate / reveal. Reads (list / get
  // / patch) carry `api_key_last4` (last 4 chars) instead, so the UI can render
  // the `bgr_...{last4}` mask without the secret ever entering the response
  // body / browser memory.
  api_key?: string;
  api_key_last4?: string;
  // The owner's on/off switch. Every agent-producing endpoint writes it, so it
  // is always present. A disabled agent receives nothing and its API key is
  // refused at connect time; `state` is then always 'offline', so the two must
  // be read together to tell "switched off" from "not connected".
  disabled: boolean;
  // Global "respond only on @mention" toggle. Default is true (mirrors
  // server users.require_mention DEFAULT true) — agent stays quiet unless
  // explicitly mentioned. Owner can flip this per-agent from My Agents.
  require_mention?: boolean;
  state?: AgentRuntimeState;
  reason?: AgentRuntimeReason;
  state_updated_at?: number;
  // Task metadata the server fills in while the agent is busy or idle.
  last_task_id?: string;
  last_task_started_at?: number;
  last_task_finished_at?: number;
  last_connected_plugin_id?: string;
}

// AL-4 (#313 v0 / #379 v2) — agent_runtimes registry. Schema source:
// PR #398 migration v=16 — `agent_runtimes(id PK,
// agent_id NOT NULL UNIQUE, endpoint_url, process_kind, status,
// last_error_reason, last_heartbeat_at, created_at, updated_at)`.
// Server API: AL-4.2 — POST /agents/:id/runtime/start
// + /stop + GET surfaces a single row per agent (蓝图 §2.2 v1 边界
// "不优化多 runtime 并行" + UNIQUE(agent_id) 字面).
//
// AL-4.3 client UI consumes this shape; runtime-not-yet-registered
// agents → server 404 → UI hides the start/stop card surface entirely
// (graceful degradation, design ① "Borgee 不带 runtime": unregistered
// agents should not pretend to have a runtime).

// AgentRuntimeStatus is the four-state process-level enum (v=16 schema
// CHECK). Constraint (#321 §2): v0 does not expose 'starting' / 'stopping' /
// 'restarting' intermediate states; start/stop use the synchronous API and
// directly UPDATE status.
export type AgentRuntimeStatus = 'registered' | 'running' | 'stopped' | 'error';

// AgentRuntimeProcessKind — v1 仅 'openclaw' (蓝图 §2.2 边界字面),
// 'hermes' 占号 v2+ (CHECK 已含, schema 早就支持新值不需 v2 改 CHECK).
export type AgentRuntimeProcessKind = 'openclaw' | 'hermes';

export interface AgentRuntime {
  id: string;
  agent_id: string;
  endpoint_url: string;
  process_kind: AgentRuntimeProcessKind;
  status: AgentRuntimeStatus;
  last_error_reason: AgentRuntimeReason | null;
  last_heartbeat_at: number | null;
  created_at: number;
  updated_at: number;
}

// fetchAgentRuntime returns the runtime row for the agent, or null if
// no runtime is registered (server 404). Design ① "Borgee 不带 runtime":
// unregistered agents should not pretend to have a runtime, so the UI omits
// the card.
//
// Returns null on 404 specifically (not other errors) so the UI can
// distinguish "no runtime registered yet" (show Register CTA stub) from
// transient network failure (show retry).
export async function fetchAgentRuntime(agentId: string): Promise<AgentRuntime | null> {
  try {
    const data = await request<{ runtime: AgentRuntime }>(`/api/v1/agents/${agentId}/runtime`);
    return data.runtime;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

// startAgentRuntime / stopAgentRuntime — owner-only writes
// (RequirePermission 'agent.runtime.control', AL-4.2 server). Non-owner
// → 403; admin → 401 (ADM-0 §1.4 no admin bypass writes rule). Both endpoints
// return the updated runtime row (#379 v2 §1 拆段 AL-4.2).
export async function startAgentRuntime(agentId: string): Promise<AgentRuntime> {
  const data = await request<{ runtime: AgentRuntime }>(`/api/v1/agents/${agentId}/runtime/start`, {
    method: 'POST',
  });
  return data.runtime;
}

export async function stopAgentRuntime(agentId: string): Promise<AgentRuntime> {
  const data = await request<{ runtime: AgentRuntime }>(`/api/v1/agents/${agentId}/runtime/stop`, {
    method: 'POST',
  });
  return data.runtime;
}

export async function fetchAgents(): Promise<Agent[]> {
  const data = await request<{ agents: Agent[] }>('/api/v1/agents');
  return data.agents;
}

export interface CreateAgentResult {
  agent: Agent;
}

export async function createAgent(
  displayName: string,
  permissions?: string[],
  id?: string,
): Promise<CreateAgentResult> {
  return request<CreateAgentResult>('/api/v1/agents', {
    method: 'POST',
    body: JSON.stringify({
      display_name: displayName,
      permissions,
      ...(id ? { id } : {}),
    }),
  });
}

export async function fetchAgent(id: string): Promise<Agent> {
  const data = await request<{ agent: Agent }>(`/api/v1/agents/${id}`);
  return data.agent;
}

export async function deleteAgent(id: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/agents/${id}`, {
    method: 'DELETE',
  });
}

export type ProjectionStrategy =
  'session-brief' | 'turn-full' | 'message-only' | 'turn-thin' | 'file-brief';

export type ClaudePromptStrategy = ProjectionStrategy;

export const DEFAULT_PROJECTION_STRATEGY: ProjectionStrategy = 'turn-thin';
export const DEFAULT_CLAUDE_PROMPT_STRATEGY: ClaudePromptStrategy = DEFAULT_PROJECTION_STRATEGY;

function normalizeProjectionStrategy(value: unknown): ProjectionStrategy | undefined {
  switch (value) {
    case 'session-brief':
    case 'session-augmented':
    case 'sessionized':
      return 'session-brief';
    case 'turn-full':
    case 'inline-full':
    case 'legacy':
      return 'turn-full';
    case 'message-only':
    case 'zero-turn':
      return 'message-only';
    case 'turn-thin':
    case 'grounded-thin':
    case 'minimal-turn':
      return 'turn-thin';
    case 'projected-brief':
    case 'file-brief':
      return 'file-brief';
    default:
      return undefined;
  }
}

export interface AgentChannelSummary {
  id: string;
  name: string;
  projection_strategy?: ProjectionStrategy;
}

export async function getAgentChannels(agentId: string): Promise<AgentChannelSummary[]> {
  const data = await request<{ channels: AgentChannelSummary[] }>(
    `/api/v1/agents/${agentId}/channels`,
  );
  return data.channels.map((channel) => ({
    ...channel,
    projection_strategy:
      normalizeProjectionStrategy(channel.projection_strategy) ?? DEFAULT_PROJECTION_STRATEGY,
  }));
}

export async function setAgentChannelProjectionStrategy(
  agentId: string,
  channelId: string,
  strategy: ProjectionStrategy,
): Promise<{ channel_id: string; agent_id: string; projection_strategy: ProjectionStrategy }> {
  const data = await request<{
    channel_id: string;
    agent_id: string;
    projection_strategy: ProjectionStrategy;
  }>(`/api/v1/agents/${agentId}/channels/${channelId}/projection-strategy`, {
    method: 'PUT',
    body: JSON.stringify({ strategy }),
  });
  return {
    ...data,
    projection_strategy:
      normalizeProjectionStrategy(data.projection_strategy) ?? DEFAULT_PROJECTION_STRATEGY,
  };
}

export interface RotateAgentApiKeyResult {
  api_key: string;
}

export async function rotateAgentApiKey(id: string): Promise<RotateAgentApiKeyResult> {
  return request<RotateAgentApiKeyResult>(`/api/v1/agents/${id}/rotate-api-key`, {
    method: 'POST',
  });
}

// revealAgentApiKey — owner-rail POST /api/v1/agents/{id}/reveal-api-key.
//
// Returns the existing full plaintext key on demand (reveal ≠ rotate: the
// stored key is unchanged). Reads (fetchAgent / fetchAgents) redact the key to
// api_key_last4, so copy-to-clipboard must call this to obtain the full secret.
// POST (not GET) keeps the secret out of URLs / referer / proxy cache. Caller
// MUST NOT retain the returned key beyond the clipboard write.
export async function revealAgentApiKey(id: string): Promise<string> {
  const data = await request<{ api_key: string }>(`/api/v1/agents/${id}/reveal-api-key`, {
    method: 'POST',
  });
  return data.api_key;
}

// updateAgentRequireMention — owner-rail PATCH /api/v1/agents/{id}.
//
// Flips the global "respond only on @mention" toggle for an agent owned by
// the caller. Server collapses authz failures (cross-owner / not-yours) into
// 404 to avoid leaking existence of someone else's agent ids. Strict decode:
// the endpoint accepts only `require_mention` and `disabled`, and rejects any
// other key with 400.
//
// Returns the updated Agent so callers can re-render from authoritative
// server state rather than the optimistic local guess.
export async function updateAgentRequireMention(
  id: string,
  requireMention: boolean,
): Promise<Agent> {
  const data = await request<{ agent: Agent }>(`/api/v1/agents/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ require_mention: requireMention }),
  });
  return data.agent;
}

// setAgentDisabled — owner-rail PATCH /api/v1/agents/{id}, the on/off switch.
//
// Disabling also revokes the agent's credentials and detaches its live sockets,
// so the returned Agent is the only trustworthy post-write state. Enabling can
// answer 409 (ApiError.status), whose body carries the whole way out.
export async function setAgentDisabled(id: string, disabled: boolean): Promise<Agent> {
  const data = await request<{ agent: Agent }>(`/api/v1/agents/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ disabled }),
  });
  return data.agent;
}

export async function fetchAgentPermissions(
  id: string,
): Promise<{ permissions: string[]; details: PermissionDetail[] }> {
  return request<{ permissions: string[]; details: PermissionDetail[] }>(
    `/api/v1/agents/${id}/permissions`,
  );
}

export async function updateAgentPermissions(
  id: string,
  permissions: { permission: string; scope?: string }[],
): Promise<void> {
  await request<{ agent_id: string }>(`/api/v1/agents/${id}/permissions`, {
    method: 'PUT',
    body: JSON.stringify({ permissions }),
  });
}

// AL-2a.2 agent_configs SSOT API (#264 acceptance §4.1.a-d).
// Blueprint: agent-lifecycle.md §2.1 + plugin-protocol.md §1.4 (Borgee=SSOT
// 字段划界, blob 仅含 Borgee 管字段) + §1.5 (热更新分级 — AL-2a 走轮询
// reload, BPP frame agent_config_update 留 AL-2b + BPP-3 同合).
export interface AgentConfig {
  schema_version: number;
  blob: AgentConfigBlob;
  updated_at?: number;
}

export type HostedAgentProvider = 'claude' | 'codex' | 'copilot';

export interface HostedAgentPlacement {
  node_id: string;
  provider: HostedAgentProvider;
}

// allowedConfigKeys whitelist 跟 server-go internal/api/agent_config.go
// 同源 byte-identical (蓝图 §1.4 SSOT 字段划界).
export interface AgentConfigBlob {
  name?: string;
  avatar?: string;
  prompt?: string;
  model?: string;
  capabilities?: string[];
  enabled?: boolean;
  memory_ref?: string;
  hosted?: HostedAgentPlacement | null;
}

export async function fetchAgentConfig(id: string): Promise<AgentConfig> {
  return request<AgentConfig>(`/api/v1/agents/${id}/config`);
}

// PATCH atomic blob 整体替换 + schema_version 严格递增 (server-stamp).
// Failure surface (跟 server-go agent_config.go 同源):
//   - 400 agent_config.invalid_payload (空 body / 非 JSON / blob 缺)
//   - 400 agent_config.runtime_field_rejected (runtime-only field, fail-closed)
//   - 403 (cross-owner)
//   - 500 with msg "agent 配置保存失败, 请重试" byte-identical
export async function updateAgentConfig(id: string, blob: AgentConfigBlob): Promise<AgentConfig> {
  return request<AgentConfig>(`/api/v1/agents/${id}/config`, {
    method: 'PATCH',
    body: JSON.stringify({ blob }),
  });
}

export async function addAgentToChannel(channelId: string, agentId: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/channels/${channelId}/members`, {
    method: 'POST',
    body: JSON.stringify({ user_id: agentId }),
  });
}

// ─── Agent Files ──────────────────────────────────────────

export interface AgentFileResponse {
  content: string;
  size: number;
  mime_type: string;
  error?: undefined;
}

export async function getAgentFile(agentId: string, path: string): Promise<AgentFileResponse> {
  return request<AgentFileResponse>(
    `/api/v1/agents/${agentId}/files?path=${encodeURIComponent(path)}`,
  );
}

// ─── Agent invitations (CM-4.2) ───────────────────────
// Mirrors the hand-built sanitizer on the server (CM-4.1, see
// docs/current/server/data-model.md §Agent invitations API). `decided_at`
// and `expires_at` are omitted by the server when nil — declared optional
// here so existing pending rows decode cleanly.
export type AgentInvitationState = 'pending' | 'approved' | 'rejected' | 'expired';

export interface AgentInvitation {
  id: string;
  channel_id: string;
  agent_id: string;
  requested_by: string;
  state: AgentInvitationState;
  created_at: number;
  decided_at?: number;
  expires_at?: number;
  // Bug-029 P0: server JOIN-resolved labels. Empty string when the lookup
  // misses (agent/channel/user removed). Client falls back to raw ID.
  agent_name?: string;
  channel_name?: string;
  requester_name?: string;
}

export type AgentInvitationListRole = 'owner' | 'requester';

export async function createAgentInvitation(
  channelId: string,
  agentId: string,
  expiresAt?: number,
): Promise<AgentInvitation> {
  const data = await request<{ invitation: AgentInvitation }>('/api/v1/agent_invitations', {
    method: 'POST',
    body: JSON.stringify({
      channel_id: channelId,
      agent_id: agentId,
      ...(expiresAt !== undefined ? { expires_at: expiresAt } : {}),
    }),
  });
  return data.invitation;
}

export async function listAgentInvitations(
  role: AgentInvitationListRole = 'owner',
): Promise<AgentInvitation[]> {
  const data = await request<{ invitations: AgentInvitation[] }>(
    `/api/v1/agent_invitations?role=${role}`,
  );
  return data.invitations;
}

export async function fetchAgentInvitation(id: string): Promise<AgentInvitation> {
  const data = await request<{ invitation: AgentInvitation }>(`/api/v1/agent_invitations/${id}`);
  return data.invitation;
}

export async function decideAgentInvitation(
  id: string,
  state: 'approved' | 'rejected',
): Promise<AgentInvitation> {
  const data = await request<{ invitation: AgentInvitation }>(`/api/v1/agent_invitations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ state }),
  });
  return data.invitation;
}

// ─── Channel Files ─────────────────────────────────────

export async function listChannelFiles(
  channelId: string,
  parentId?: string,
): Promise<ChannelFile[]> {
  const qs = parentId ? `?parentId=${parentId}` : '';
  const data = await request<{ files: ChannelFile[] }>(`/api/v1/channels/${channelId}/files${qs}`);
  return data.files;
}

export async function uploadChannelFile(
  channelId: string,
  file: UploadFile,
  parentId?: string,
): Promise<ChannelFile> {
  const qs = parentId ? `?parentId=${parentId}` : '';
  const data = await request<{ file: ChannelFile }>(
    `/api/v1/channels/${channelId}/files/upload${qs}`,
    {
      method: 'POST',
      body: { kind: 'multipart', parts: [multipartFile('file', file)] },
    },
  );
  return data.file;
}

export async function downloadChannelFile(
  channelId: string,
  fileId: string,
): Promise<ApiBytesResponse> {
  const res = await requestResponse(`/api/v1/channels/${channelId}/files/${fileId}`, {
    responseType: 'bytes',
  });
  if (res.status < 200 || res.status >= 300)
    throw new ApiError(res.status, res.statusText || 'Download failed');
  if (res.kind !== 'bytes') throw new Error('File download transport did not return bytes');
  return res;
}

export async function updateChannelFile(
  channelId: string,
  fileId: string,
  content: string,
): Promise<ChannelFile> {
  const data = await request<{ file: ChannelFile }>(
    `/api/v1/channels/${channelId}/files/${fileId}`,
    {
      method: 'PUT',
      body: JSON.stringify({ content }),
    },
  );
  return data.file;
}

export async function deleteChannelFile(channelId: string, fileId: string): Promise<void> {
  const res = await requestResponse(`/api/v1/channels/${channelId}/files/${fileId}`, {
    method: 'DELETE',
  });
  if (res.status < 200 || res.status >= 300) throw new ApiError(res.status, 'Delete failed');
}

export async function createChannelDirectory(
  channelId: string,
  name: string,
  parentId?: string,
): Promise<ChannelFile> {
  const data = await request<{ file: ChannelFile }>(`/api/v1/channels/${channelId}/files/mkdir`, {
    method: 'POST',
    body: JSON.stringify({ name, parentId }),
  });
  return data.file;
}

export async function renameChannelFile(
  channelId: string,
  fileId: string,
  name: string,
): Promise<ChannelFile> {
  const data = await request<{ file: ChannelFile }>(
    `/api/v1/channels/${channelId}/files/${fileId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    },
  );
  return data.file;
}

// ─── Remote Nodes ─────────────────────────────────────

export interface RemoteNode {
  id: string;
  machine_name: string;
  last_seen_at: string | null;
  created_at: string;
}

export interface RemoteNodeHostedAgent {
  id: string;
  display_name: string;
  provider: HostedAgentProvider;
  enabled: boolean;
  disabled: boolean;
  last_connected_plugin_id?: string;
}

export interface RemoteBinding {
  id: string;
  node_id: string;
  channel_id: string;
  path: string;
  label: string | null;
  created_at: string;
  machine_name?: string;
  node_user_id?: string;
}

export interface RemoteDirEntry {
  name: string;
  isDirectory: boolean;
  size: number;
  mtime: string;
}

export async function fetchRemoteNodes(): Promise<RemoteNode[]> {
  const data = await request<{ nodes: RemoteNode[] }>('/api/v1/remote/nodes');
  return data.nodes;
}

// createRemoteNode returns the node AND its connection token. The server surfaces
// the plaintext token in exactly two responses — this one and
// rotateRemoteNodeConnectionToken — and strips it from list/get/status
// (json:"-"), so a caller that needs the token must capture it from one of them.
export async function createRemoteNode(
  machineName: string,
): Promise<{ node: RemoteNode; connectionToken: string }> {
  const data = await request<{ node: RemoteNode; connection_token: string }>(
    '/api/v1/remote/nodes',
    {
      method: 'POST',
      body: JSON.stringify({ machine_name: machineName }),
    },
  );
  return { node: data.node, connectionToken: data.connection_token };
}

export async function deleteRemoteNode(nodeId: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/remote/nodes/${nodeId}`, {
    method: 'DELETE',
  });
}

// rotateRemoteNodeConnectionToken issues a fresh connection token and invalidates
// the old one at once, force-closing the node's live WebSocket. The machine stays
// offline until the install command is re-run there with the returned token.
export async function rotateRemoteNodeConnectionToken(nodeId: string): Promise<string> {
  const data = await request<{ connection_token: string }>(
    `/api/v1/remote/nodes/${nodeId}/rotate-token`,
    {
      method: 'POST',
    },
  );
  return data.connection_token;
}

export async function fetchRemoteBindings(nodeId: string): Promise<RemoteBinding[]> {
  const data = await request<{ bindings: RemoteBinding[] }>(
    `/api/v1/remote/nodes/${nodeId}/bindings`,
  );
  return data.bindings;
}

export async function fetchRemoteNodeHostedAgents(
  nodeId: string,
): Promise<RemoteNodeHostedAgent[]> {
  const data = await request<{ agents: RemoteNodeHostedAgent[] }>(
    `/api/v1/remote/nodes/${nodeId}/hosted-agents`,
  );
  return data.agents;
}

export async function createRemoteBinding(
  nodeId: string,
  channelId: string,
  path: string,
  label?: string,
): Promise<RemoteBinding> {
  const data = await request<{ binding: RemoteBinding }>(
    `/api/v1/remote/nodes/${nodeId}/bindings`,
    {
      method: 'POST',
      body: JSON.stringify({ channel_id: channelId, path, label }),
    },
  );
  return data.binding;
}

export async function deleteRemoteBinding(nodeId: string, bindingId: string): Promise<void> {
  await request<{ ok: boolean }>(`/api/v1/remote/nodes/${nodeId}/bindings/${bindingId}`, {
    method: 'DELETE',
  });
}

export async function fetchChannelRemoteBindings(channelId: string): Promise<RemoteBinding[]> {
  const data = await request<{ bindings: RemoteBinding[] }>(
    `/api/v1/channels/${channelId}/remote-bindings`,
  );
  return data.bindings;
}

export async function remoteLs(
  nodeId: string,
  path: string,
): Promise<{ entries: RemoteDirEntry[] }> {
  return request<{ entries: RemoteDirEntry[] }>(
    `/api/v1/remote/nodes/${nodeId}/ls?path=${encodeURIComponent(path)}`,
  );
}

export async function remoteReadFile(
  nodeId: string,
  path: string,
): Promise<{ content: string; mimeType: string; size: number }> {
  return request<{ content: string; mimeType: string; size: number }>(
    `/api/v1/remote/nodes/${nodeId}/read?path=${encodeURIComponent(path)}`,
  );
}

export interface RemoteNodeStatus {
  online: boolean;
  exposed_dirs: string[] | null;
  capabilities?: string[] | null;
}

export async function fetchRemoteNodeStatus(nodeId: string): Promise<RemoteNodeStatus> {
  return request<RemoteNodeStatus>(`/api/v1/remote/nodes/${nodeId}/status`);
}

export interface RepositoryMapping {
  repository: string;
  path: string;
}

export async function fetchRepositoryMappings(nodeId: string): Promise<RepositoryMapping[]> {
  const data = await request<{ mappings: RepositoryMapping[] }>(
    `/api/v1/remote/nodes/${nodeId}/repository-mappings`,
  );
  return data.mappings;
}

export async function bindRepositoryMapping(
  nodeId: string,
  repository: string,
  path: string,
): Promise<RepositoryMapping> {
  const data = await request<{ mapping: RepositoryMapping }>(
    `/api/v1/remote/nodes/${nodeId}/repository-mappings`,
    { method: 'POST', body: JSON.stringify({ repository, path }) },
  );
  return data.mapping;
}

async function checkRepositoryMapping(
  nodeId: string,
  repository: string,
  action: 'verify' | 'resolve',
): Promise<RepositoryMapping> {
  const data = await request<{ mapping: RepositoryMapping }>(
    `/api/v1/remote/nodes/${nodeId}/repository-mappings/${action}`,
    { method: 'POST', body: JSON.stringify({ repository }) },
  );
  return data.mapping;
}

export const verifyRepositoryMapping = (nodeId: string, repository: string) =>
  checkRepositoryMapping(nodeId, repository, 'verify');

export const resolveRepositoryMapping = (nodeId: string, repository: string) =>
  checkRepositoryMapping(nodeId, repository, 'resolve');

export async function removeRepositoryMapping(nodeId: string, repository: string): Promise<void> {
  await request<{ ok: boolean }>(
    `/api/v1/remote/nodes/${nodeId}/repository-mappings?repository=${encodeURIComponent(repository)}`,
    { method: 'DELETE' },
  );
}

// ─── Commands ──────────────────────────────────────────

export interface AgentCommandInfo {
  agent_id: string;
  agent_name: string;
  commands: Array<{
    name: string;
    description: string;
    usage: string;
    params: Array<{ name: string; type: string; required?: boolean; placeholder?: string }>;
  }>;
}

export interface CommandsResponse {
  builtin: Array<{ name: string; description: string; usage: string }>;
  agent: AgentCommandInfo[];
}

export async function listCommands(channelId?: string): Promise<CommandsResponse> {
  const params = new URLSearchParams();
  if (channelId) params.set('channelId', channelId);
  const qs = params.toString();
  return request<CommandsResponse>(`/api/v1/commands${qs ? `?${qs}` : ''}`);
}

// ─── Agent activities ──────────────────────────────────
//
// The cold start for the activity rail. Everything here is held in the
// server's memory and written to no table, so this answer speaks only for the
// moment it was composed and there is nothing behind it to reconcile against.

/** One tool call an agent ran or is running. Kind and status are open enums, relayed rather than validated. */
export interface AgentActivitySnapshotEntry {
  id: string;
  label?: string;
  description?: string;
  kind?: string;
  status: string;
  detail?: string;
  paths?: string[];
  parent_id?: string;
  subagent?: boolean;
  reason?: string;
}

/** One agent's work in one channel. `sequence` is that agent's own counter, not a position in any durable sequence. */
export interface AgentActivitySnapshot {
  agent_id: string;
  state: 'working' | 'waiting' | 'idle';
  sequence: number;
  plan?: AgentActivityPlanEntry[];
  activities?: AgentActivitySnapshotEntry[];
  answer?: string;
  reasoning?: string;
}

export async function fetchChannelAgentActivities(
  channelId: string,
): Promise<{ agents: AgentActivitySnapshot[] }> {
  return request<{ agents: AgentActivitySnapshot[] }>(
    `/api/v1/channels/${channelId}/agent-activities`,
  );
}

// ─── CHN-3.2 user_channel_layout (CHN-3.3 client) ──────────
//
// Spec: docs/implementation/modules/chn-3-spec.md §1 CHN-3.2 段 + §0
// 设计 ② 个人偏好两维 collapsed + position. Server: api/layout.go
// (#412, stacked off CHN-3.1 schema #410 v=19).
//
// 设计 ④ 反约束 错码 byte-identical: server PUT 对 DM channel_id 返
// 400 with code `layout.dm_not_grouped` (5 源 #357/#353/#366/#402/
// #412); client 走 GET pull, PUT batch upsert, 不挂 push frame
// (设计 ⑥ ordering client 端事).

export interface LayoutRow {
  channel_id: string;
  collapsed: number; // 0 | 1 (BOOL); position is REAL.
  position: number;
  created_at?: number;
  updated_at?: number;
}

/** GET /me/layout — 本人 layout list (position ASC ordering). */
export async function getMyLayout(): Promise<{ layout: LayoutRow[] }> {
  return request<{ layout: LayoutRow[] }>(`/api/v1/me/layout`);
}

/**
 * PUT /me/layout — batch upsert (collapsed + position 两维, server 跑
 * ON CONFLICT (user_id, channel_id) DO UPDATE atomic). 反约束: DM
 * channel_id → 400 `layout.dm_not_grouped` (server 兜底).
 */
export async function putMyLayout(layout: LayoutRow[]): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/v1/me/layout`, {
    method: 'PUT',
    body: JSON.stringify({ layout }),
  });
}

/**
 * BPP-3.2.2 — POST /api/v1/me/grants
 *
 * Owner one-click capability grant (or reject/snooze) from the three buttons
 * in the owner DM's SystemMessageBubble.
 *
 * Returns the server response. action='grant' writes user_permissions;
 * action='reject'/'snooze' writes a structured application log only, with no
 * durable audit or deny-list state.
 */
export interface MeGrantRequest {
  agent_id: string;
  capability: string;
  scope: string;
  request_id: string;
  action: 'grant' | 'reject' | 'snooze';
}
export interface MeGrantResponse {
  granted: boolean;
  action: 'grant' | 'reject' | 'snooze';
  agent_id?: string;
  capability?: string;
  scope?: string;
}
export async function postMeGrant(req: MeGrantRequest): Promise<MeGrantResponse> {
  const resp = await requestResponse(`/api/v1/me/grants`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (resp.status < 200 || resp.status >= 300) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = jsonData<{ error_code?: string }>(resp);
      if (body?.error_code) detail = body.error_code;
    } catch {
      /* ignore */
    }
    throw new Error(`me/grants ${detail}`);
  }
  return jsonData<MeGrantResponse>(resp) as MeGrantResponse;
}

// AL-5.2 — owner agent error recovery (POST /api/v1/agents/:id/recover).
export interface AL5RecoverPayload {
  action: 'recover';
  agent_id: string;
  reason: string;
  request_id: string;
}
export interface AL5RecoverResponse {
  state: string;
  reason: string;
}
export async function postAgentRecover(req: AL5RecoverPayload): Promise<AL5RecoverResponse> {
  const resp = await requestResponse(`/api/v1/agents/${encodeURIComponent(req.agent_id)}/recover`, {
    method: 'POST',
    body: JSON.stringify({ request_id: req.request_id }),
  });
  if (resp.status < 200 || resp.status >= 300) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = jsonData<{ error?: string }>(resp);
      if (body?.error) detail = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(`agents/recover ${detail}`);
  }
  return jsonData<AL5RecoverResponse>(resp) as AL5RecoverResponse;
}

// DM-4.1 — agent message edit 多端同步.
// PATCH /api/v1/channels/{channelId}/messages/{messageId}
//
// 设计 (跟 dm-4-spec.md §0):
//   ① 复用 RT-3 既有 fan-out (events INSERT op="edit" + Hub broadcast)
//   ② edit 是 cursor 子集 (cursor 进展归 useDMSync DM-3 #508)
//   ③ thinking 5-pattern 反约束延伸第 3 处 (机械修订, 不暴露 reasoning)
export interface DM4EditResponse {
  message: {
    id: string;
    channel_id: string;
    sender_id: string;
    content: string;
    edited_at?: number | null;
    [key: string]: unknown;
  };
}

export async function patchDMMessage(
  channelID: string,
  messageID: string,
  content: string,
): Promise<DM4EditResponse> {
  const resp = await requestResponse(
    `/api/v1/channels/${encodeURIComponent(channelID)}/messages/${encodeURIComponent(messageID)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    },
  );
  if (resp.status < 200 || resp.status >= 300) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = jsonData<{ error?: string }>(resp);
      if (body?.error) detail = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(`dm/edit ${detail}`);
  }
  return jsonData<DM4EditResponse>(resp) as DM4EditResponse;
}

// DM-7.3 — edit history GET (sender-only user-rail).
// Server: GET /api/v1/channels/{channelId}/messages/{messageId}/edit-history
export interface DM7EditHistoryEntry {
  old_content: string;
  ts: number;
  reason: string;
}

export interface DM7EditHistoryResponse {
  history: DM7EditHistoryEntry[];
}

export async function getEditHistory(
  channelID: string,
  messageID: string,
): Promise<DM7EditHistoryResponse> {
  const resp = await requestResponse(
    `/api/v1/channels/${encodeURIComponent(channelID)}/messages/${encodeURIComponent(messageID)}/edit-history`,
    { method: 'GET' },
  );
  if (resp.status < 200 || resp.status >= 300) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = jsonData<{ error?: string }>(resp);
      if (body?.error) detail = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(`dm7/edit-history ${detail}`);
  }
  return jsonData<DM7EditHistoryResponse>(resp) as DM7EditHistoryResponse;
}

// CHN-14.3 — channel description edit history GET (owner-only user-rail).
// Server: GET /api/v1/channels/{channelId}/description/history
// (跟 DM-7 #558 EditHistoryEntry 一样 byte-identical: old_content/ts/reason).
export interface CHN14DescriptionHistoryEntry {
  old_content: string;
  ts: number;
  reason: string;
}

export interface CHN14DescriptionHistoryResponse {
  history: CHN14DescriptionHistoryEntry[];
}

export async function getChannelDescriptionHistory(
  channelID: string,
): Promise<CHN14DescriptionHistoryResponse> {
  const resp = await requestResponse(
    `/api/v1/channels/${encodeURIComponent(channelID)}/description/history`,
    { method: 'GET' },
  );
  if (resp.status < 200 || resp.status >= 300) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = jsonData<{ error?: string }>(resp);
      if (body?.error) detail = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(`chn14/description-history ${detail}`);
  }
  return jsonData<CHN14DescriptionHistoryResponse>(resp) as CHN14DescriptionHistoryResponse;
}

// CHN-10: set channel description (owner-only). Server: PUT
// /api/v1/channels/{channelId}/description body {description}; 500 char
// upper bound (DESCRIPTION_MAX_LENGTH byte-identical 跟 server const +
// channels.topic GORM size:500 同源 — 改一处 = 改三处反向锁守门).
export const DESCRIPTION_MAX_LENGTH = 500;

export async function setChannelDescription(
  channelId: string,
  description: string,
): Promise<Channel> {
  const data = await request<{ channel: Channel }>(
    `/api/v1/channels/${encodeURIComponent(channelId)}/description`,
    {
      method: 'PUT',
      body: JSON.stringify({ description }),
    },
  );
  return data.channel;
}

// RT-4: get channel presence (member-only — server filters channel.members
// ∩ IsOnline). Returns array of online user_id; 0 client-side caching v0.
export interface ChannelPresenceResponse {
  online_user_ids: string[];
  counted_at: number;
}

export async function getChannelPresence(channelId: string): Promise<ChannelPresenceResponse> {
  return request<ChannelPresenceResponse>(
    `/api/v1/channels/${encodeURIComponent(channelId)}/presence`,
  );
}

/**
 * Ask one agent to stop the turn it is running in one channel.
 *
 * `aborted` is the whole answer: true means a running turn was found and
 * abandoned, false means none was. false deliberately does not tell "finished a
 * moment ago" apart from "never started" or from "this runtime implements no
 * stopping at all", so the copy shown for it has to say only "nothing to stop".
 */
export interface StopAgentTurnResponse {
  aborted: boolean;
}

export async function stopAgentTurn(
  channelId: string,
  agentId: string,
): Promise<StopAgentTurnResponse> {
  return request<StopAgentTurnResponse>(
    `/api/v1/channels/${encodeURIComponent(channelId)}/agents/${encodeURIComponent(agentId)}/stop-turn`,
    { method: 'POST' },
  );
}

// ──────────────────────────────────────────────────────────────────────
// CHN-15 (#chn-15): channel readonly toggle — owner-only.
// ──────────────────────────────────────────────────────────────────────

/**
 * CHANNEL_READONLY_TOAST — byte-identical 跟 server
 * `internal/api/chn_15_readonly.go::ChannelErrCodeReadonlyNoSend` const +
 * `docs/qa/chn-15-content-lock.md` §3 同源.
 *
 * 改 = 改三处: server const + 此 map + content-lock §3.
 */
export const CHANNEL_READONLY_TOAST: Record<string, string> = {
  'channel.readonly_no_send': '只读频道, 仅创建者可发言',
};

export interface ChannelReadonlyResponse {
  channel_id: string;
  collapsed: number;
  readonly: boolean;
}

/** PUT /api/v1/channels/{id}/readonly — set channel readonly (owner-only). */
export async function setChannelReadonly(channelID: string): Promise<ChannelReadonlyResponse> {
  const resp = await requestResponse(`/api/v1/channels/${encodeURIComponent(channelID)}/readonly`, {
    method: 'PUT',
  });
  if (resp.status < 200 || resp.status >= 300) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = jsonData<{ error?: string }>(resp);
      if (body?.error) detail = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(`channel/readonly ${detail}`);
  }
  return jsonData<ChannelReadonlyResponse>(resp) as ChannelReadonlyResponse;
}

/** DELETE /api/v1/channels/{id}/readonly — unset channel readonly (idempotent). */
export async function unsetChannelReadonly(channelID: string): Promise<ChannelReadonlyResponse> {
  const resp = await requestResponse(`/api/v1/channels/${encodeURIComponent(channelID)}/readonly`, {
    method: 'DELETE',
  });
  if (resp.status < 200 || resp.status >= 300) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = jsonData<{ error?: string }>(resp);
      if (body?.error) detail = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(`channel/readonly ${detail}`);
  }
  return jsonData<ChannelReadonlyResponse>(resp) as ChannelReadonlyResponse;
}
